import React, { useEffect, useState } from 'react';
import AdsCard from '../AdsCard';
import linkedin from '../../../../assests/linkedin_1.svg';
import {
  fetchAuthCodeFromLinkedIn,
  fetchCredentials,
  fetchLinkedInAdAccounts,
  getlinkedinAdsCampaignList,
} from '../../../../services/ApiService';
import {
  clearLinkedInOAuthState,
  clearOAuthCallbackParams,
  getOAuthRedirectUri,
} from '../../../../utils/oauthRedirect';
import { getIntegrationErrorMessage } from '../../../../utils/oauthErrors';
import { toast } from 'react-toastify';
import { CommonModal } from '../../../CommonModal';
import { LinkedInAdsAccTable } from './LinkedInAdsAccTable';
import UnassociatedCampainModal from './UnassociatedCampainModal';
import { useNavigate } from 'react-router-dom';

const linkedInOAuthDevLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[LinkedIn OAuth Dev]', ...args);
  }
};

const getLinkedInAccountType = (socialMediaType) =>
  socialMediaType === 'organic_social' ? 'posts' : 'ads';

const LinkedInAdsIntegration = ({
  isAccountLinked,
  fetchAdsAccountStatus,
  accountDetails,
  social_media_type,
}) => {
  const navigate = useNavigate();
  const expectedAccountType = getLinkedInAccountType(social_media_type);

  const [connecting, setConnecting] = useState(false);
  const [linkedInAdAccounts, setLinkedInAdAccounts] = useState([]);
  const [showLinkedInAdAccModal, setShowLinkedInAdAccModal] = useState(false);
  const [loadingUnAssociatedCampaigns, setLoadingUnAssociatedCampaigns] = useState(false);
  const [showUnassociatedCampainModal, setShowUnassociatedCampainModal] = useState(false);
  const [campaignList, setCampaignList] = useState([]);

  const finishOAuthCallback = () => {
    clearLinkedInOAuthState();
    clearOAuthCallbackParams(navigate);
  };

  const handleConnect = async () => {
    setConnecting(true);
    localStorage.setItem('selected_platform_to_connect', 'linkedin');
    localStorage.setItem('account_type', expectedAccountType);
    localStorage.removeItem('linkedin_code');
    localStorage.removeItem('code');

    const redirectUri = getOAuthRedirectUri();
    linkedInOAuthDevLog('Connect clicked', {
      card: social_media_type,
      account_type: expectedAccountType,
      redirect_uri: redirectUri,
    });

    if (!redirectUri) {
      setConnecting(false);
      toast.error('LinkedIn redirect URL is not configured.');
      return;
    }

    const credentials = await fetchCredentials();

    if (credentials?.linkedin?.linkedin_ads_client_id) {
      fetchAuthCodeFromLinkedIn(
        credentials.linkedin.linkedin_ads_client_id,
        redirectUri,
        credentials?.linkedin?.linkedin_ads_scope,
      );
      return;
    }

    setConnecting(false);
    toast.error('Error connecting to LinkedIn');
  };

  const continueConnection = async (code) => {
    setConnecting(true);
    linkedInOAuthDevLog('continueConnection started', {
      card: social_media_type,
      account_type: expectedAccountType,
      code_prefix: `${code?.slice(0, 12)}...`,
    });

    try {
      const result = await fetchLinkedInAdAccounts(code, expectedAccountType);

      if (result?.account_type !== expectedAccountType) {
        linkedInOAuthDevLog('Ignored callback for different account type', {
          expected: expectedAccountType,
          received: result?.account_type,
        });
        return;
      }

      if (expectedAccountType === 'posts') {
        toast.success(result?.message || 'LinkedIn account linked successfully.');
        fetchAdsAccountStatus?.();
        finishOAuthCallback();
        return;
      }

      const adAccounts = Array.isArray(result?.data) ? result.data : [];

      if (adAccounts.length > 0) {
        setLinkedInAdAccounts(adAccounts);
        setShowLinkedInAdAccModal(true);
        clearOAuthCallbackParams(navigate);
        return;
      }

      toast.error(
        'No LinkedIn ad accounts found on this profile. You need a LinkedIn Campaign Manager ad account.',
      );
      finishOAuthCallback();
    } catch (error) {
      console.error('[LinkedIn OAuth Dev] Connect failed', error);
      toast.error(getIntegrationErrorMessage(error, 'Failed to connect to LinkedIn.'));
      finishOAuthCallback();
    } finally {
      setConnecting(false);
    }
  };

  const handleShowUnAssociatedCampaigns = async () => {
    if (!isAccountLinked) {
      return;
    }

    setLoadingUnAssociatedCampaigns(true);
    try {
      const result = await getlinkedinAdsCampaignList();

      if (result?.campaigns?.length > 0) {
        setCampaignList(result.campaigns);
        setShowUnassociatedCampainModal(true);
      } else {
        setCampaignList([]);
        toast.error(
          'No new campaigns were found. Please ensure that campaigns have been created or try again later.',
        );
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoadingUnAssociatedCampaigns(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    const selectedPlatform = localStorage.getItem('selected_platform_to_connect');
    const storedAccountType = localStorage.getItem('account_type');

    linkedInOAuthDevLog('OAuth callback check', {
      card: social_media_type,
      expected_account_type: expectedAccountType,
      has_code: Boolean(code),
      selected_platform_to_connect: selectedPlatform,
      stored_account_type: storedAccountType,
    });

    if (selectedPlatform !== 'linkedin' || storedAccountType !== expectedAccountType) {
      return;
    }

    if (error) {
      toast.error(
        errorDescription ||
          `LinkedIn authorization failed. Add this redirect URL in LinkedIn Developer Portal: ${getOAuthRedirectUri()}`,
      );
      finishOAuthCallback();
      setConnecting(false);
      return;
    }

    if (code) {
      continueConnection(code);
    }
  }, []);

  return (
    <>
      <AdsCard
        handleConnect={handleConnect}
        isAccountLinked={isAccountLinked || false}
        platform={social_media_type === 'organic_social' ? 'LinkedIn' : 'LinkedIn Ads'}
        platform_icon={linkedin}
        connecting={connecting}
        description="LinkedIn is a professional networking platform that helps people connect with colleagues, clients, and industry leaders to grow their career or business."
        handleShowUnAssociatedCampaigns={handleShowUnAssociatedCampaigns}
        loadingUnAssociatedCampaigns={loadingUnAssociatedCampaigns}
        accountDetails={accountDetails}
        social_media_type={social_media_type}
        fetchAdsAccountStatus={fetchAdsAccountStatus}
      />

      <CommonModal
        show={showLinkedInAdAccModal}
        setShow={setShowLinkedInAdAccModal}
        title={
          <div className="d-flex flex-column gap-1">
            <span className="fs-4 fw-bold">Your LinkedIn Ads Accounts</span>
            <small className="fs-8">Select an account to proceed with integration.</small>
          </div>
        }
        body={
          <LinkedInAdsAccTable
            fetchAdsAccountStatus={fetchAdsAccountStatus}
            linkedInAdAccounts={linkedInAdAccounts}
            setShowLinkedInAdAccModal={setShowLinkedInAdAccModal}
            onLinked={finishOAuthCallback}
          />
        }
      />

      <UnassociatedCampainModal
        showUnassociatedCampainModal={showUnassociatedCampainModal}
        setShowUnassociatedCampainModal={setShowUnassociatedCampainModal}
        campaignList={campaignList}
      />
    </>
  );
};

export default LinkedInAdsIntegration;
